import { Link } from 'react-router-dom';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-dark-900">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-dark-900/80 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <img src="/logo.png" alt="ZEPHYR AI" className="w-10 h-10 rounded-2xl" />
            <span className="text-xl font-bold">ZEPHYR AI</span>
          </Link>
          <Link to="/" className="text-gray-400 hover:text-white transition">
            ← Back to Home
          </Link>
        </div>
      </nav>

      {/* Terms of Service Content */}
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold mb-6">Terms of Service</h1>
          <p className="text-gray-400 mb-12">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

          <div className="prose prose-invert prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">1. Acceptance of Terms</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                By accessing and using ZEPHYR AI mobile application ("the App"), you accept and agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use the App.
              </p>
              <p className="text-gray-300 leading-relaxed mb-4">
                These Terms constitute a legally binding agreement between you and Zeynsoft ("we," "us," or "our"). We reserve the right to modify these Terms at any time. Continued use of the App after changes constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">2. Eligibility and Age Requirements</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                You must be at least 13 years of age to use this App. If you are between 13 and 18 years old (or the age of legal majority in your jurisdiction), you may only use the App with the consent and supervision of a parent or legal guardian.
              </p>
              <p className="text-gray-300 leading-relaxed">
                By using the App, you represent and warrant that you meet these eligibility requirements. We reserve the right to verify your age and terminate accounts that do not meet these requirements.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">3. Account Registration and Security</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                To access certain features, you may need to create an account. You agree to:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain and promptly update your account information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized access or security breach</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                You may not share your account credentials or transfer your account to another person. We reserve the right to suspend or terminate accounts that violate these requirements.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">4. AI-Powered Features and Services</h2>

              <h3 className="text-xl font-semibold mb-3 text-white">4.1 AI Image Processing</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                ZEPHYR AI uses artificial intelligence and machine learning technologies to process, edit, and enhance your images. You acknowledge and agree that:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>AI-generated results may vary in quality and accuracy</li>
                <li>Results depend on input image quality and content</li>
                <li>AI processing requires cloud connectivity for certain features</li>
                <li>Processing times may vary based on server load and network conditions</li>
                <li>We do not guarantee specific results or outcomes</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-white">4.2 AI Model Training</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                By default, images you upload are not used to train or improve our AI models. However, if you explicitly opt-in to our improvement program, you grant us the right to use your submitted images for model training purposes. You may opt-out at any time through the App settings.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">5. Acceptable Use Policy</h2>

              <h3 className="text-xl font-semibold mb-3 text-white">5.1 Permitted Uses</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                You may use the App for lawful purposes only, including:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Personal photo editing and enhancement</li>
                <li>Creating content for personal or commercial use (subject to subscription tier)</li>
                <li>Editing images you own or have permission to use</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-white">5.2 Prohibited Content and Activities</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                You agree NOT to upload, create, or distribute content that:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Depicts minors in inappropriate or harmful situations</li>
                <li>Contains nudity, sexually explicit material, or pornography</li>
                <li>Infringes upon intellectual property rights of others</li>
                <li>Violates privacy rights or publicity rights of individuals</li>
                <li>Creates deepfakes or misleading impersonations without consent</li>
                <li>Promotes hate speech, violence, discrimination, or illegal activities</li>
                <li>Contains malware, viruses, or malicious code</li>
                <li>Generates false or misleading information for fraudulent purposes</li>
                <li>Impersonates real individuals without their explicit consent</li>
                <li>Is defamatory, harassing, threatening, or abusive</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-white">5.3 Consent for Image Use</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                When uploading images containing recognizable individuals:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                <li>You must have explicit consent from all individuals depicted</li>
                <li>You are responsible for obtaining necessary permissions and releases</li>
                <li>You may not upload images of individuals without their knowledge and consent</li>
                <li>Special care must be taken with images of public figures and celebrities</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">6. Intellectual Property Rights</h2>

              <h3 className="text-xl font-semibold mb-3 text-white">6.1 Your Content</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                You retain all ownership rights to images you upload to the App ("Your Content"). By uploading content, you grant us a limited, non-exclusive, worldwide license to:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Process, edit, and enhance your images as requested</li>
                <li>Store your images temporarily for processing purposes</li>
                <li>Display processed results back to you</li>
              </ul>
              <p className="text-gray-300 leading-relaxed mb-4">
                This license terminates when you delete the content from the App or close your account.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-white">6.2 AI-Generated Outputs</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Subject to these Terms and applicable law, you own the AI-enhanced or AI-generated images produced by the App. However, you acknowledge that:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Similar outputs may be generated for other users from similar inputs</li>
                <li>Ownership claims may be limited by evolving AI copyright laws</li>
                <li>You are responsible for compliance with applicable copyright and trademark laws</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-white">6.3 Our Intellectual Property</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                The App, including its design, features, AI models, algorithms, code, and documentation, is owned by Zeynsoft and protected by copyright, trademark, and other intellectual property laws. You may not:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                <li>Reverse engineer, decompile, or disassemble the App</li>
                <li>Copy, modify, or create derivative works of the App</li>
                <li>Remove or alter any copyright notices or proprietary markings</li>
                <li>Use our trademarks, logos, or branding without written permission</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">7. Subscription and Payment Terms</h2>

              <h3 className="text-xl font-semibold mb-3 text-white">7.1 Free and Premium Features</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                The App offers both free and premium subscription tiers:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li><strong>Free Account:</strong> Limited daily AI credits, basic features, personal use only</li>
                <li><strong>Premium Subscription:</strong> Unlimited or increased AI credits, advanced features, commercial use rights, priority processing</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-white">7.2 Billing and Renewal</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Premium subscriptions are billed on a recurring basis (monthly or annually). By subscribing, you agree that:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Your subscription will automatically renew unless cancelled</li>
                <li>You must cancel at least 24 hours before the next billing date</li>
                <li>Subscription fees are non-refundable except as required by law</li>
                <li>We may change subscription prices with 30 days' notice</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-white">7.3 AI Credits System</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Certain AI features consume credits. Free accounts receive limited daily credits that reset every 24 hours. Premium subscribers receive increased or unlimited credits. Unused free credits do not roll over to the next day.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">8. Content Moderation and Enforcement</h2>

              <h3 className="text-xl font-semibold mb-3 text-white">8.1 Automated Content Filtering</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                We employ AI-powered safety filters to detect and block prohibited content. Images flagged by our safety systems will not be processed, and repeated violations may result in account suspension.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-white">8.2 Account Suspension and Termination</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                We reserve the right to:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Suspend or terminate accounts that violate these Terms</li>
                <li>Remove or refuse to process content that violates our policies</li>
                <li>Report illegal content to law enforcement authorities</li>
                <li>Deny refunds for terminated accounts due to policy violations</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-white">8.3 Appeals Process</h3>
              <p className="text-gray-300 leading-relaxed">
                If you believe your content was wrongly flagged or your account was unfairly suspended, you may submit an appeal to our support team at help@zeynsoft.com with relevant details. We will review appeals within 7 business days.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">9. Disclaimers and Limitations of Liability</h2>

              <h3 className="text-xl font-semibold mb-3 text-white">9.1 "AS IS" Service</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR ACCURACY OF AI OUTPUTS.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-white">9.2 AI Output Disclaimer</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                We do not guarantee the accuracy, quality, or suitability of AI-generated outputs. You are solely responsible for reviewing and verifying all AI-processed content before use. We disclaim all liability for:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Inaccurate or unexpected AI results</li>
                <li>Loss or corruption of uploaded images</li>
                <li>Service interruptions or downtime</li>
                <li>Third-party AI model failures or changes</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-white">9.3 Limitation of Liability</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, ZEYNSOFT SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR BUSINESS OPPORTUNITIES ARISING FROM:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Your use or inability to use the App</li>
                <li>Unauthorized access to or alteration of your content</li>
                <li>Third-party conduct or content</li>
                <li>Any claim related to AI-generated outputs</li>
              </ul>
              <p className="text-gray-300 leading-relaxed mb-4">
                OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM, OR $100 USD, WHICHEVER IS GREATER.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">10. Indemnification</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                You agree to indemnify, defend, and hold harmless Zeynsoft, its officers, directors, employees, and agents from any claims, liabilities, damages, losses, or expenses (including reasonable attorneys' fees) arising from:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                <li>Your violation of these Terms</li>
                <li>Your violation of any third-party rights, including intellectual property or privacy rights</li>
                <li>Your use of AI-generated content in violation of applicable laws</li>
                <li>Content you upload or create using the App</li>
                <li>Your negligent or wrongful conduct</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">11. Saving Your Edits</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                After editing your photos, remember to save them to your device:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Download edited images directly to your photo gallery</li>
                <li>Share your creations to social media or cloud storage</li>
                <li>Keep backup copies of your favorite edits</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                Processed images are available for immediate download. We recommend saving your work as the app focuses on real-time editing rather than long-term storage.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">12. Third-Party Services and Links</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                The App may integrate with or link to third-party services, including:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Payment processors (Google Play, Apple App Store)</li>
                <li>Analytics services (Google Analytics, Firebase)</li>
                <li>Advertising networks (Google AdMob)</li>
                <li>Cloud storage providers</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                We are not responsible for third-party services' privacy practices, terms, or availability. Your use of third-party services is governed by their respective terms and policies.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">13. Dispute Resolution and Governing Law</h2>

              <h3 className="text-xl font-semibold mb-3 text-white">13.1 Governing Law</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the Republic of Turkey, without regard to its conflict of law provisions.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-white">13.2 Dispute Resolution</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                In the event of any dispute arising from these Terms or your use of the App, you agree to first attempt to resolve the dispute informally by contacting us at help@zeynsoft.com. If the dispute cannot be resolved within 30 days, you agree to submit to the exclusive jurisdiction of the courts of Mersin, Turkey.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-white">13.3 Class Action Waiver</h3>
              <p className="text-gray-300 leading-relaxed">
                You agree to resolve disputes with us on an individual basis only, and not as part of any class or representative action. You waive any right to participate in a class action lawsuit or class-wide arbitration.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">14. Modifications to Terms</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We reserve the right to modify these Terms at any time. When we make material changes, we will:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Update the "Last updated" date at the top of this page</li>
                <li>Notify you via email or in-app notification</li>
                <li>Provide at least 30 days' notice for material changes</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                Your continued use of the App after changes become effective constitutes acceptance of the modified Terms. If you do not agree to the changes, you must stop using the App and delete your account.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">15. Termination</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                You may terminate your account at any time through the App settings or by contacting us. Upon termination:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Your access to premium features will immediately cease</li>
                <li>Your content may be deleted from our servers</li>
                <li>No refunds will be issued for unused subscription periods</li>
                <li>Sections of these Terms that by their nature should survive will continue to apply</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                We may terminate or suspend your account immediately, without notice, for violations of these Terms or for any other reason at our sole discretion.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">16. Severability</h2>
              <p className="text-gray-300 leading-relaxed">
                If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary, and the remaining provisions will remain in full force and effect.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">17. Entire Agreement</h2>
              <p className="text-gray-300 leading-relaxed">
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and Zeynsoft regarding the App and supersede all prior agreements and understandings, whether written or oral.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">18. Contact Information</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                If you have any questions, concerns, or feedback about these Terms, please contact us:
              </p>
              <div className="bg-dark-800 border border-white/10 rounded-xl p-6">
                <ul className="text-gray-300 space-y-2">
                  <li><strong>Company:</strong> Zeynsoft</li>
                  <li><strong>Email:</strong> info@zeynsoft.com</li>
                  <li><strong>Support:</strong> help@zeynsoft.com</li>
                  <li><strong>Website:</strong> <a href="https://zephyrai.zeynsoft.com" className="text-primary hover:text-primary/80 transition">https://zephyrai.zeynsoft.com</a></li>
                  <li><strong>Address:</strong> Yenişehir, Mersin, Turkey</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-4 text-white">19. Acknowledgment</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                BY USING THE APP, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE.
              </p>
              <p className="text-gray-300 leading-relaxed">
                If you do not accept these Terms, you must immediately cease all use of the App and uninstall it from your device.
              </p>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6">
        <div className="max-w-7xl mx-auto text-center text-gray-400">
          <p>© {new Date().getFullYear()} ZEPHYR AI by Zeynsoft. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
